import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import ReactFlow, {
  Controls,
  MiniMap,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Panel,
  ReactFlowProvider,
  useReactFlow,
  Handle,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import KnowledgeGraph3D from "./KnowledgeGraph3D";
import {
  getKnowledgeGraphStats,
  getKnowledgeGraphGraphData,
  getKnowledgeGraphEvents,
  getKnowledgeGraphSymbols,
  getCurrencyNarratives,
  getCausalChain,
  getKnowledgeGraphPatterns,
} from "../../api/api";
import API_BASE_URL from "../../api/config";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import PageBackButton from "../../components/PageBackButton";
import {
  ChevronLeft,
  ChevronRight,
  Search,
  BarChart3,
  GitBranch,
  Globe,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  X,
  Maximize2,
  Minimize2,
  Filter,
  Zap,
  Activity,
  Layers,
  Brain,
  Shield,
  Repeat,
  Box,
  Monitor,
} from "lucide-react";

// ── Helpers ────────────────────────────────────────────────────
const stripHtml = (html) => {
  if (!html) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

// ── Constants ──────────────────────────────────────────────────
const EVENT_TYPES = [
  { value: "", label: "All Types" },
  { value: "MACRO_RELEASE", label: "Economic Release" },
  { value: "CENTRAL_BANK", label: "Central Bank" },
  { value: "PRICE_MOVE", label: "Price Move" },
  { value: "POSITIONING", label: "Positioning" },
  { value: "NEWS", label: "News" },
  { value: "GEOPOLITICAL", label: "Geopolitical" },
  { value: "SENTIMENT_SHIFT", label: "Sentiment" },
  { value: "INSIDER_TRADING", label: "Insider Trading" },
];

const CURRENCIES = [
  "", "USD", "EUR", "GBP", "JPY", "AUD", "CAD", "CHF", "NZD",
];

const SYMBOL_PAGE_SIZE = 30;

const EVENT_TYPE_COLORS = {
  MACRO_RELEASE: "#3b82f6",
  CENTRAL_BANK: "#8b5cf6",
  PRICE_MOVE: "#f59e0b",
  POSITIONING: "#10b981",
  NEWS: "#6366f1",
  GEOPOLITICAL: "#ef4444",
  SENTIMENT_SHIFT: "#ec4899",
  INSIDER_TRADING: "#f97316",
};

const EVENT_TYPE_BG = {
  MACRO_RELEASE: "bg-blue-500/20 text-blue-400",
  CENTRAL_BANK: "bg-purple-500/20 text-purple-400",
  PRICE_MOVE: "bg-amber-500/20 text-amber-400",
  POSITIONING: "bg-emerald-500/20 text-emerald-400",
  NEWS: "bg-indigo-500/20 text-indigo-400",
  GEOPOLITICAL: "bg-red-500/20 text-red-400",
  SENTIMENT_SHIFT: "bg-pink-500/20 text-pink-400",
  INSIDER_TRADING: "bg-orange-500/20 text-orange-400",
};

const EDGE_TYPE_META = {
  TRIGGERED:    { label: "Triggered",    color: "#f59e0b", desc: "Macro surprise → price reaction" },
  REINFORCED:   { label: "Reinforced",   color: "#10b981", desc: "Corroborating data releases" },
  CONTRADICTED: { label: "Contradicted", color: "#8b5cf6", desc: "Conflicting data signals" },
  AMPLIFIED:    { label: "Amplified",    color: "#3b82f6", desc: "Positioning confirmed by price" },
  DAMPENED:     { label: "Dampened",     color: "#ec4899", desc: "Positioning vs contrary price" },
  CAUSED:       { label: "Caused",       color: "#ef4444", desc: "Direct cause-effect" },
  PRECEDED:     { label: "Preceded",     color: "#6b7280", desc: "Temporal sequence" },
  FOLLOWED_BY:  { label: "Followed By",  color: "#6b7280", desc: "Reverse temporal sequence" },
};

const ALL_EDGE_TYPES = Object.keys(EDGE_TYPE_META);


// ── Custom ReactFlow Node (compact) ────────────────────────────
function EventNode({ data, selected }) {
  const shortDate = useMemo(() => {
    try {
      return new Date(data.timestamp).toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch { return ""; }
  }, [data.timestamp]);

  return (
    <div
      className={`rounded border shadow-sm transition-all
        ${selected ? "ring-2 ring-primary border-primary scale-105" : "border-borderDefault"}
        bg-backgroundSurface cursor-pointer relative`}
      style={{
        borderLeftWidth: 3,
        borderLeftColor: data.color,
        padding: "4px 8px",
        minWidth: 140,
        maxWidth: 200,
      }}
    >
      <Handle type="target" position={Position.Top} className="!w-1.5 !h-1.5 !bg-transparent !border-0" />
      <Handle type="source" position={Position.Bottom} className="!w-1.5 !h-1.5 !bg-transparent !border-0" />
      <Handle type="target" position={Position.Left} id="left" className="!w-1.5 !h-1.5 !bg-transparent !border-0" />
      <Handle type="source" position={Position.Right} id="right" className="!w-1.5 !h-1.5 !bg-transparent !border-0" />

      <div className="flex items-center gap-1 mb-0.5">
        <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: data.color }} />
        <span className="text-[9px] font-semibold uppercase text-textSecondary truncate">
          {data.eventTypeDisplay}
        </span>
      </div>
      <p className="text-[11px] font-medium text-textPrimary leading-tight line-clamp-1">
        {data.title}
      </p>
      <div className="flex items-center justify-between mt-0.5">
        <span className="text-[9px] text-textSecondary">{shortDate}</span>
        {data.currencies?.length > 0 && (
          <span className="text-[8px] text-primary font-medium">{data.currencies.slice(0, 2).join(",")}</span>
        )}
      </div>
    </div>
  );
}

const nodeTypes = { eventNode: EventNode, dateLabel: DateLabelNode, laneLabel: LaneLabelNode };

// ── Date label node (time axis marker) ─────────────────────────
function DateLabelNode({ data }) {
  return (
    <div className="px-2 py-1 text-center select-none pointer-events-none">
      <div className="text-[10px] font-semibold text-primary/80 tracking-wide whitespace-nowrap">
        {data.label}
      </div>
      <div className="mx-auto mt-1 w-px h-3 bg-primary/30" />
    </div>
  );
}

// ── Lane label node (swim-lane row indicator) ──────────────────
function LaneLabelNode({ data }) {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 select-none pointer-events-none whitespace-nowrap">
      <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: data.color }} />
      <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: data.color }}>
        {data.label}
      </span>
    </div>
  );
}


// ── Helpers ────────────────────────────────────────────────────
function formatTime(ts) {
  try {
    return new Date(ts).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return ts || "-"; }
}

function SentimentBadge({ score }) {
  if (score > 0.05) return <span className="inline-flex items-center gap-0.5 text-success font-semibold"><TrendingUp size={14} /> +{score.toFixed(2)}</span>;
  if (score < -0.05) return <span className="inline-flex items-center gap-0.5 text-destructive font-semibold"><TrendingDown size={14} /> {score.toFixed(2)}</span>;
  return <span className="inline-flex items-center gap-0.5 text-textSecondary font-semibold"><Minus size={14} /> {score.toFixed(2)}</span>;
}

function StatCard({ icon, label, value, color, subtitle }) {
  return (
    <Card className="p-4 flex items-center gap-3">
      <div className={`p-2 rounded-lg ${color}`}>{icon}</div>
      <div>
        <p className="text-xs text-textSecondary">{label}</p>
        <p className="text-xl font-bold text-textPrimary">{typeof value === "number" ? value.toLocaleString() : value}</p>
        {subtitle && <p className="text-[10px] text-textSecondary mt-0.5">{subtitle}</p>}
      </div>
    </Card>
  );
}

function ConfidenceBar({ value, size = "sm", showLabel = true }) {
  const pct = Math.round((value || 0) * 100);
  const color = pct >= 80 ? "bg-emerald-500" : pct >= 60 ? "bg-amber-500" : pct >= 40 ? "bg-orange-500" : "bg-red-500";
  const h = size === "sm" ? "h-1.5" : "h-2.5";
  return (
    <div className="flex items-center gap-2">
      <div className={`flex-1 ${h} bg-secondary rounded-full overflow-hidden`}>
        <div className={`${h} ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      {showLabel && <span className="text-[10px] text-textSecondary font-medium w-8 text-right">{pct}%</span>}
    </div>
  );
}


// ── Main Page Component ────────────────────────────────────────
export default function KnowledgeGraphPage() {
  // ── State ──
  const [stats, setStats] = useState(null);
  const [narratives, setNarratives] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [events, setEvents] = useState([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [graphData, setGraphData] = useState(null);
  const [chainData, setChainData] = useState(null);

  const [loading, setLoading] = useState({ stats: false, graph: false, events: false, narratives: false, chain: false, patterns: false });
  const [error, setError] = useState(null);

  // Filters
  const [eventType, setEventType] = useState("");
  const [currency, setCurrency] = useState("");
  const [symbol, setSymbol] = useState("");
  const [daysBack, setDaysBack] = useState(90);
  const [graphLimit, setGraphLimit] = useState(100);
  const [granularity, setGranularity] = useState("");
  const [moveType, setMoveType] = useState("");
  const [eventsPage, setEventsPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [narrativeTimeframe, setNarrativeTimeframe] = useState("daily");

  // UI
  const [activeTab, setActiveTab] = useState("graph"); // graph | events | narratives
  const [graphExpanded, setGraphExpanded] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [showChainModal, setShowChainModal] = useState(false);
  const [activeEdgeTypes, setActiveEdgeTypes] = useState(new Set(ALL_EDGE_TYPES));
  const [viewMode, setViewMode] = useState("3d"); // "2d" | "3d"
  const [graphLayoutMode, setGraphLayoutMode] = useState("relationships");
  const [graphNodeSizeMode, setGraphNodeSizeMode] = useState("relationships");

  // ReactFlow
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // ── Data fetching ──
  const fetchStats = useCallback(async () => {
    setLoading((l) => ({ ...l, stats: true }));
    try {
      const data = await getKnowledgeGraphStats();
      setStats(data);
    } catch (e) {
      console.error("Failed to load stats:", e);
    } finally {
      setLoading((l) => ({ ...l, stats: false }));
    }
  }, []);

  const fetchGraphData = useCallback(async () => {
    setLoading((l) => ({ ...l, graph: true }));
    setError(null);
    try {
      const data = await getKnowledgeGraphGraphData({
        currency: currency || undefined,
        symbol: symbol || undefined,
        event_type: eventType || undefined,
        days_back: daysBack,
        limit: graphLimit,
        granularity: granularity || undefined,
        move_type: moveType || undefined,
      });
      setGraphData(data);
      setNodes(data.nodes || []);
      setEdges(data.edges || []);
    } catch (e) {
      setError("Failed to load graph data");
      console.error(e);
    } finally {
      setLoading((l) => ({ ...l, graph: false }));
    }
  }, [currency, symbol, eventType, daysBack, graphLimit, granularity, moveType, setNodes, setEdges]);

  const fetchEvents = useCallback(async (page = 1) => {
    setLoading((l) => ({ ...l, events: true }));
    try {
      const dateFrom = daysBack > 0
        ? new Date(Date.now() - daysBack * 86400000).toISOString().slice(0, 10)
        : undefined;
      const data = await getKnowledgeGraphEvents({
        event_type: eventType || undefined,
        currency: currency || undefined,
        symbol: symbol || undefined,
        search: searchTerm || undefined,
        date_from: dateFrom,
        page,
        page_size: 50,
      });
      if (data && typeof data.count === "number") {
        setEvents(data.results);
        setTotalEvents(data.count);
      } else {
        setEvents(Array.isArray(data) ? data : []);
        setTotalEvents(0);
      }
    } catch (e) {
      console.error("Failed to load events:", e);
    } finally {
      setLoading((l) => ({ ...l, events: false }));
    }
  }, [eventType, currency, symbol, searchTerm, daysBack]);

  const fetchNarratives = useCallback(async () => {
    setLoading((l) => ({ ...l, narratives: true }));
    try {
      const data = await getCurrencyNarratives({ timeframe: narrativeTimeframe });
      setNarratives(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load narratives:", e);
    } finally {
      setLoading((l) => ({ ...l, narratives: false }));
    }
  }, [narrativeTimeframe]);

  const fetchChain = useCallback(async (eventId) => {
    setLoading((l) => ({ ...l, chain: true }));
    try {
      const data = await getCausalChain(eventId, { direction: "both", max_depth: 3 });
      setChainData(data);
      setShowChainModal(true);
    } catch (e) {
      console.error("Failed to load causal chain:", e);
    } finally {
      setLoading((l) => ({ ...l, chain: false }));
    }
  }, []);

  const fetchPatterns = useCallback(async () => {
    setLoading((l) => ({ ...l, patterns: true }));
    try {
      const data = await getKnowledgeGraphPatterns({ days_back: daysBack || 90, min_occurrences: 3 });
      setPatterns(data?.patterns || []);
    } catch (e) {
      console.error("Failed to load patterns:", e);
    } finally {
      setLoading((l) => ({ ...l, patterns: false }));
    }
  }, [daysBack]);

  // ── Initial load ──
  useEffect(() => {
    fetchStats();
    fetchNarratives();
  }, [fetchStats, fetchNarratives]);

  useEffect(() => {
    if (activeTab === "graph") fetchGraphData();
  }, [activeTab, fetchGraphData]);

  useEffect(() => {
    if (activeTab === "events") fetchEvents(1);
  }, [activeTab, fetchEvents]);

  useEffect(() => {
    if (activeTab === "patterns") fetchPatterns();
  }, [activeTab, fetchPatterns]);

  // ── Available event types (only those with data) ──
  const availableEventTypes = useMemo(() => {
    if (!stats?.event_counts) return EVENT_TYPES;
    return EVENT_TYPES.filter(
      (t) => t.value === "" || (stats.event_counts[t.value] && stats.event_counts[t.value] > 0)
    );
  }, [stats]);

  // ── ReactFlow event handlers ──
  const onNodeClick = useCallback((_event, node) => {
    if (node.type === "dateLabel" || node.type === "laneLabel") return;
    setSelectedEventId(node.data.id);
  }, []);

  const onNodeDoubleClick = useCallback((_event, node) => {
    if (node.type === "dateLabel" || node.type === "laneLabel") return;
    fetchChain(node.data.id);
  }, [fetchChain]);

  // Pagination
  const eventsPageSize = 50;
  const totalEventsPages = Math.max(1, Math.ceil(totalEvents / eventsPageSize));

  const goToEventsPage = (p) => {
    if (p < 1 || p > totalEventsPages) return;
    setEventsPage(p);
    fetchEvents(p);
  };

  // ── Sorted narratives ──
  const sortedNarratives = useMemo(() => {
    return [...narratives].sort((a, b) => {
      // Sort by confluence first (high confluence = more actionable), then by sentiment
      const confDiff = (b.confluence_score || 0) - (a.confluence_score || 0);
      if (Math.abs(confDiff) > 0.1) return confDiff;
      return Math.abs(b.sentiment_score) - Math.abs(a.sentiment_score);
    });
  }, [narratives]);


  return (
    <div className="max-w-[1600px] mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-start gap-3">
          <PageBackButton label="Back to Research" to="/research" preferHistory={false} hideLabelOnMobile />
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <GitBranch className="text-primary" size={24} />
              Knowledge Graph
            </h1>
            <p className="text-sm text-textSecondary mt-0.5">
              Market cause-and-effect relationships — {stats ? stats.total_events?.toLocaleString() : "..."} events, {stats ? stats.total_edges?.toLocaleString() : "..."} edges
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { fetchStats(); if (activeTab === "graph") fetchGraphData(); else if (activeTab === "events") fetchEvents(eventsPage); fetchNarratives(); }}
          disabled={loading.stats || loading.graph}
        >
          <RefreshCw size={14} className={`mr-1 ${(loading.stats || loading.graph) ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Row */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <StatCard icon={<Activity size={18} />} label="Total Events" value={stats.total_events} color="bg-blue-500/20 text-blue-400" />
          <StatCard icon={<GitBranch size={18} />} label="Causal Edges" value={stats.total_edges} color="bg-amber-500/20 text-amber-400"
            subtitle={`${stats.edge_method_counts?.RULE || 0} Rule · ${stats.edge_method_counts?.LLM || 0} AI`}
          />
          <StatCard icon={<Globe size={18} />} label="Entities" value={stats.total_entities} color="bg-emerald-500/20 text-emerald-400" />
          <StatCard icon={<Brain size={18} />} label="AI Processed" value={stats.llm_processed || 0} color="bg-violet-500/20 text-violet-400"
            subtitle={stats.total_news ? `${Math.round(((stats.llm_processed || 0) / stats.total_news) * 100)}% of ${stats.total_news} news` : undefined}
          />
          <StatCard icon={<Shield size={18} />} label="Avg Confidence" value={stats.avg_confidence?.RULE ? `${Math.round(stats.avg_confidence.RULE * 100)}%` : "—"} color="bg-cyan-500/20 text-cyan-400"
            subtitle={stats.avg_confidence?.LLM ? `AI: ${Math.round(stats.avg_confidence.LLM * 100)}%` : undefined}
          />
          <StatCard icon={<BarChart3 size={18} />} label="Currencies" value={stats.currency_sentiments?.length || 0} color="bg-purple-500/20 text-purple-400" />
        </div>
      )}

      {/* Currency Sentiment Strip */}
      {stats?.currency_sentiments?.length > 0 && (
        <Card className="p-3">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={16} className="text-primary" />
            <span className="text-sm font-medium text-textPrimary">Currency Sentiment (14d)</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {stats.currency_sentiments.sort((a, b) => b.sentiment_score - a.sentiment_score).map((s) => (
              <button
                key={s.currency}
                onClick={() => { setCurrency(s.currency); setActiveTab("graph"); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                  ${currency === s.currency ? "bg-primary/20 border border-primary" : "bg-secondary/50 border border-transparent hover:bg-secondary"}`}
              >
                <span className="font-bold">{s.currency}</span>
                <SentimentBadge score={s.sentiment_score} />
                <span className="text-[10px] text-textSecondary">({s.event_count})</span>
              </button>
            ))}
          </div>
        </Card>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-borderDefault overflow-x-auto scrollbar-hide -mx-1 px-1">
        {[
          { key: "graph", label: "Graph View", icon: <GitBranch size={14} /> },
          { key: "events", label: "Events Table", icon: <Activity size={14} /> },
          { key: "narratives", label: "Currency Narratives", icon: <BarChart3 size={14} /> },
          { key: "patterns", label: "Patterns", icon: <Repeat size={14} /> },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap flex-shrink-0
              ${activeTab === tab.key
                ? "border-primary text-primary"
                : "border-transparent text-textSecondary hover:text-textPrimary"
              }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "graph" && (
        <GraphTab
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          onNodeDoubleClick={onNodeDoubleClick}
          loading={loading.graph}
          graphData={graphData}
          currency={currency}
          setCurrency={setCurrency}
          symbol={symbol}
          setSymbol={setSymbol}
          eventType={eventType}
          setEventType={setEventType}
          daysBack={daysBack}
          setDaysBack={setDaysBack}
          graphLimit={graphLimit}
          setGraphLimit={setGraphLimit}
          granularity={granularity}
          setGranularity={setGranularity}
          moveType={moveType}
          setMoveType={setMoveType}
          fetchGraphData={fetchGraphData}
          expanded={graphExpanded}
          setExpanded={setGraphExpanded}
          selectedEventId={selectedEventId}
          setSelectedEventId={setSelectedEventId}
          onExploreChain={fetchChain}
          loadingChain={loading.chain}
          activeEdgeTypes={activeEdgeTypes}
          setActiveEdgeTypes={setActiveEdgeTypes}
          availableEventTypes={availableEventTypes}
          viewMode={viewMode}
          setViewMode={setViewMode}
          graphLayoutMode={graphLayoutMode}
          setGraphLayoutMode={setGraphLayoutMode}
          graphNodeSizeMode={graphNodeSizeMode}
          setGraphNodeSizeMode={setGraphNodeSizeMode}
        />
      )}

      {activeTab === "events" && (
        <EventsTab
          events={events}
          loading={loading.events}
          totalEvents={totalEvents}
          page={eventsPage}
          totalPages={totalEventsPages}
          goToPage={goToEventsPage}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          eventType={eventType}
          setEventType={setEventType}
          currency={currency}
          setCurrency={setCurrency}
          symbol={symbol}
          setSymbol={setSymbol}
          daysBack={daysBack}
          fetchEvents={() => { setEventsPage(1); fetchEvents(1); }}
          onExploreChain={fetchChain}
          loadingChain={loading.chain}
          availableEventTypes={availableEventTypes}
        />
      )}

      {activeTab === "narratives" && (
        <NarrativesTab
          narratives={sortedNarratives}
          loading={loading.narratives}
          timeframe={narrativeTimeframe}
          setTimeframe={setNarrativeTimeframe}
          fetchNarratives={fetchNarratives}
        />
      )}

      {activeTab === "patterns" && (
        <PatternsTab patterns={patterns} loading={loading.patterns} fetchPatterns={fetchPatterns} />
      )}

      {/* Causal Chain Modal */}
      {showChainModal && chainData && (
        <ChainModal
          chainData={chainData}
          onClose={() => { setShowChainModal(false); setChainData(null); }}
        />
      )}
    </div>
  );
}


// ── GraphCanvas (inner component that has access to useReactFlow) ──
function GraphCanvas({ nodes, edges, onNodesChange, onEdgesChange, onNodeClick, onNodeDoubleClick, graphData, activeEdgeTypes }) {
  const reactFlowInstance = useReactFlow();
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [edgeTooltip, setEdgeTooltip] = useState(null);

  // Filter edges by active edge types
  const visibleEdges = useMemo(() => {
    return edges.filter((e) => activeEdgeTypes.has(e.data?.edgeType));
  }, [edges, activeEdgeTypes]);

  // Auto-fit when nodes change
  useEffect(() => {
    if (nodes.length > 0 && reactFlowInstance) {
      const timer = setTimeout(() => {
        reactFlowInstance.fitView({ padding: 0.12, duration: 400 });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [nodes.length, reactFlowInstance]);

  // Highlight edges connected to hovered node
  const styledEdges = useMemo(() => {
    if (!hoveredNodeId) return visibleEdges;
    return visibleEdges.map((e) => {
      const connected = e.source === hoveredNodeId || e.target === hoveredNodeId;
      return {
        ...e,
        style: {
          ...e.style,
          opacity: connected ? 1 : 0.1,
          strokeWidth: connected ? 3 : 1,
        },
        animated: false,
        label: connected ? e.data?.edgeTypeDisplay : undefined,
        labelStyle: connected ? { fill: "#e2e8f0", fontSize: 10, fontWeight: 600 } : undefined,
        labelBgStyle: connected ? { fill: "#1e293b", fillOpacity: 0.9 } : undefined,
        labelBgPadding: [4, 2],
        zIndex: connected ? 10 : 0,
      };
    });
  }, [visibleEdges, hoveredNodeId]);

  // Dim non-connected nodes when hovering
  const styledNodes = useMemo(() => {
    if (!hoveredNodeId) return nodes;
    const connectedIds = new Set([hoveredNodeId]);
    visibleEdges.forEach((e) => {
      if (e.source === hoveredNodeId) connectedIds.add(e.target);
      if (e.target === hoveredNodeId) connectedIds.add(e.source);
    });
    return nodes.map((n) => {
      // Label nodes always stay fully visible
      if (n.type === "dateLabel" || n.type === "laneLabel") return n;
      return {
        ...n,
        style: {
          ...n.style,
          opacity: connectedIds.has(n.id) ? 1 : 0.25,
          transition: "opacity 0.2s",
        },
      };
    });
  }, [nodes, visibleEdges, hoveredNodeId]);

  const onNodeMouseEnter = useCallback((_, node) => {
    if (node.type === "dateLabel" || node.type === "laneLabel") return;
    setHoveredNodeId(node.id);
  }, []);

  const onNodeMouseLeave = useCallback(() => {
    setHoveredNodeId(null);
  }, []);

  const onEdgeMouseEnter = useCallback((event, edge) => {
    setEdgeTooltip({
      x: event.clientX,
      y: event.clientY,
      type: edge.data?.edgeTypeDisplay || edge.data?.edgeType,
      confidence: edge.data?.confidence,
      description: edge.data?.description,
      color: edge.data?.color,
      extractionMethod: edge.data?.extractionMethod,
    });
  }, []);

  const onEdgeMouseLeave = useCallback(() => {
    setEdgeTooltip(null);
  }, []);

  return (
    <>
      <ReactFlow
        nodes={styledNodes}
        edges={styledEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onNodeDoubleClick={onNodeDoubleClick}
        onNodeMouseEnter={onNodeMouseEnter}
        onNodeMouseLeave={onNodeMouseLeave}
        onEdgeMouseEnter={onEdgeMouseEnter}
        onEdgeMouseLeave={onEdgeMouseLeave}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.12 }}
        attributionPosition="bottom-left"
        minZoom={0.02}
        maxZoom={3}
        defaultEdgeOptions={{
          type: "smoothstep",
          markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12 },
          style: { strokeWidth: 2 },
        }}
        edgesUpdatable={false}
        edgesFocusable
        proOptions={{ hideAttribution: true }}
      >
        <Controls showInteractive={false} />
        <MiniMap
          nodeColor={(n) => {
            if (n.type === "dateLabel" || n.type === "laneLabel") return "transparent";
            return n.data?.color || "#6b7280";
          }}
          style={{ backgroundColor: "rgba(15,24,38,0.85)", borderRadius: 8 }}
          maskColor="rgba(15,24,38,0.6)"
        />
        <Background color="#1e293b" gap={30} size={1} />
        <Panel position="top-left">
          <div className="bg-backgroundSurface/90 rounded-lg border border-borderDefault px-3 py-2 text-xs text-textSecondary backdrop-blur">
            {graphData?.meta?.total_events || 0} nodes · {graphData?.meta?.total_edges || 0} edges
            {graphData?.meta?.currency_filter && <span className="ml-2 text-primary font-medium">{graphData.meta.currency_filter}</span>}
            {graphData?.meta?.symbol_filter && <span className="ml-2 text-amber-400 font-medium">{graphData.meta.symbol_filter}</span>}
            <span className="ml-2 opacity-60">Hover → highlight · Click → detail · Double-click → causal chain</span>
          </div>
        </Panel>
      </ReactFlow>

      {/* Edge hover tooltip */}
      {edgeTooltip && (
        <div
          className="fixed z-[9999] pointer-events-none bg-backgroundSurface border border-borderDefault rounded-lg shadow-xl px-3 py-2 text-xs"
          style={{ left: edgeTooltip.x + 12, top: edgeTooltip.y - 10 }}
        >
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2.5 h-0.5 rounded" style={{ backgroundColor: edgeTooltip.color || "#6b7280" }} />
            <span className="font-semibold text-textPrimary">{edgeTooltip.type}</span>
            {edgeTooltip.extractionMethod === "LLM" && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-400 border border-violet-500/30">
                AI
              </span>
            )}
          </div>
          {edgeTooltip.confidence != null && (
            <p className="text-textSecondary">Confidence: {(edgeTooltip.confidence * 100).toFixed(0)}%</p>
          )}
          {edgeTooltip.description && (
            <p className="text-textSecondary mt-0.5 max-w-[200px] line-clamp-2">{edgeTooltip.description}</p>
          )}
          {edgeTooltip.extractionMethod && (
            <p className="text-textSecondary mt-0.5 text-[10px] opacity-70">Source: {edgeTooltip.extractionMethod}</p>
          )}
        </div>
      )}
    </>
  );
}


// ── Graph Tab ──────────────────────────────────────────────────
function normalizeSymbolInput(value) {
  return String(value || "").trim().toUpperCase().replace(/[/_\s]/g, "");
}


function SymbolAutocomplete({
  value,
  onChange,
  daysBack,
  currency,
  eventType,
}) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [symbols, setSymbols] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const rootRef = useRef(null);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    setPage(1);
    setSymbols([]);
    setTotal(0);
  }, [query, currency, eventType, daysBack]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const timeout = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await getKnowledgeGraphSymbols({
          search: query || undefined,
          page,
          page_size: SYMBOL_PAGE_SIZE,
          days_back: daysBack,
          currency: currency || undefined,
          event_type: eventType || undefined,
        });
        if (cancelled) return;
        const rows = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
        setSymbols((prev) => {
          if (page === 1) return rows;
          const merged = new Map(prev.map((item) => [item.symbol, item]));
          rows.forEach((item) => merged.set(item.symbol, item));
          return [...merged.values()];
        });
        setTotal(Number(data?.count || rows.length || 0));
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load knowledge graph symbols:", err);
          setSymbols([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 220);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [open, query, page, daysBack, currency, eventType]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  const selectedSymbol = normalizeSymbolInput(value);
  const hasMoreSymbols = symbols.length < total;

  const selectSymbol = useCallback((symbol) => {
    const clean = normalizeSymbolInput(symbol);
    onChange(clean);
    setQuery(clean);
    setOpen(false);
  }, [onChange]);

  const clearSymbol = useCallback(() => {
    onChange("");
    setQuery("");
    setPage(1);
    setOpen(false);
  }, [onChange]);

  const handleResultsScroll = useCallback((event) => {
    if (loading || !hasMoreSymbols) return;
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 40) {
      setPage((p) => p + 1);
    }
  }, [hasMoreSymbols, loading]);

  return (
    <div ref={rootRef} className="relative min-w-[230px]">
      <Input
        value={query}
        placeholder="Search symbol"
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          const nextQuery = event.target.value;
          setQuery(nextQuery);
          if (!nextQuery.trim() && value) onChange("");
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            if (symbols[0]) selectSymbol(symbols[0].symbol);
            else if (query.trim()) selectSymbol(query);
          }
          if (event.key === "Escape") setOpen(false);
        }}
        className="h-9 pr-8 text-sm placeholder:text-textSecondary"
      />
      {selectedSymbol && (
        <button
          type="button"
          onClick={clearSymbol}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-textSecondary hover:text-textPrimary"
          title="Clear symbol"
        >
          <X size={13} />
        </button>
      )}

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-80 overflow-hidden rounded-lg border border-borderDefault bg-backgroundBody shadow-xl">
          <div className="max-h-80 overflow-y-auto py-1" onScroll={handleResultsScroll}>
            {loading && symbols.length === 0 ? (
              <div className="px-3 py-3 text-center text-xs text-textSecondary">
                <RefreshCw size={14} className="inline animate-spin mr-1" />
                Loading symbols...
              </div>
            ) : symbols.length === 0 ? (
              <div className="px-3 py-3 text-xs text-textSecondary">No symbols found</div>
            ) : symbols.map((item) => (
              <button
                key={item.symbol}
                type="button"
                onClick={() => selectSymbol(item.symbol)}
                className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-secondary/60 ${
                  selectedSymbol === item.symbol ? "bg-primary/10 text-primary" : "text-textPrimary"
                }`}
              >
                <span className="font-semibold tracking-wide">{item.label || item.symbol}</span>
                <span className="text-[11px] text-textSecondary">
                  {(item.event_count || 0).toLocaleString()} events
                </span>
              </button>
            ))}
            {loading && symbols.length > 0 && (
              <div className="px-3 py-2 text-center text-[11px] text-textSecondary">
                <RefreshCw size={12} className="inline animate-spin mr-1" />
                Loading more...
              </div>
            )}
            {!loading && hasMoreSymbols && symbols.length > 0 && (
              <div className="px-3 py-2 text-center text-[11px] text-textSecondary">
                Scroll for more
              </div>
            )}
            {!loading && !hasMoreSymbols && symbols.length > 0 && (
              <div className="px-3 py-2 text-center text-[11px] text-textSecondary">
                {total.toLocaleString()} symbols
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function GraphTab({
  nodes, edges, onNodesChange, onEdgesChange,
  onNodeClick, onNodeDoubleClick,
  loading, graphData,
  currency, setCurrency, symbol, setSymbol, eventType, setEventType,
  daysBack, setDaysBack, graphLimit, setGraphLimit,
  granularity, setGranularity, moveType, setMoveType,
  fetchGraphData, expanded, setExpanded,
  selectedEventId, setSelectedEventId, onExploreChain, loadingChain,
  activeEdgeTypes, setActiveEdgeTypes,
  availableEventTypes = EVENT_TYPES,
  viewMode = "3d", setViewMode,
  graphLayoutMode = "relationships", setGraphLayoutMode,
  graphNodeSizeMode = "relationships", setGraphNodeSizeMode,
}) {
  const selectedEvent = selectedEventId
    ? nodes.find((n) => n.data.id === selectedEventId)?.data
    : null;

  // Count edges by type for the current graph
  const edgeTypeCounts = useMemo(() => {
    const counts = {};
    (edges || []).forEach((e) => {
      const t = e.data?.edgeType || "UNKNOWN";
      counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  }, [edges]);

  // Get edges connected to selected event
  const connectedEdges = useMemo(() => {
    if (!selectedEventId) return [];
    return (edges || []).filter(
      (e) => e.source === String(selectedEventId) || e.target === String(selectedEventId)
    );
  }, [edges, selectedEventId]);

  // Toggle an edge type
  const toggleEdgeType = useCallback((type) => {
    setActiveEdgeTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, [setActiveEdgeTypes]);

  const clearGraphFilters = useCallback(() => {
    setCurrency("");
    setSymbol("");
    setEventType("");
    setDaysBack(90);
    setGraphLimit(100);
    setGranularity("");
    setMoveType("");
    setActiveEdgeTypes(new Set(ALL_EDGE_TYPES));
    setSelectedEventId(null);
  }, [
    setActiveEdgeTypes,
    setCurrency,
    setSymbol,
    setDaysBack,
    setEventType,
    setGranularity,
    setGraphLimit,
    setMoveType,
    setSelectedEventId,
  ]);

  const hasGraphFilters = currency || symbol || eventType || daysBack !== 90 || graphLimit !== 100
    || granularity || moveType || activeEdgeTypes.size !== ALL_EDGE_TYPES.length;
  const expandedGraphHeight = typeof window !== "undefined"
    ? Math.max(420, window.innerHeight - 156)
    : 700;

  useEffect(() => {
    if (!expanded || typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [expanded]);

  const filterPanel = (
    <Card className={`p-3 ${expanded ? "shrink-0 border-borderDefault bg-backgroundSurface/95 shadow-xl backdrop-blur" : ""}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={14} className="text-textSecondary" />
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="bg-secondary text-textPrimary text-sm rounded-lg px-3 py-1.5 border border-borderDefault"
          >
            <option value="">All Currencies</option>
            {CURRENCIES.filter(Boolean).map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <SymbolAutocomplete
            value={symbol}
            onChange={setSymbol}
            daysBack={daysBack}
            currency={currency}
            eventType={eventType}
          />
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            className="bg-secondary text-textPrimary text-sm rounded-lg px-3 py-1.5 border border-borderDefault"
          >
            {availableEventTypes.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <select
            value={daysBack}
            onChange={(e) => setDaysBack(Number(e.target.value))}
            className="bg-secondary text-textPrimary text-sm rounded-lg px-3 py-1.5 border border-borderDefault"
          >
            {[7, 14, 30, 60, 90].map((d) => (
              <option key={d} value={d}>Last {d} days</option>
            ))}
            <option value={0}>All Time</option>
          </select>
          <select
            value={graphLimit}
            onChange={(e) => setGraphLimit(Number(e.target.value))}
            className="bg-secondary text-textPrimary text-sm rounded-lg px-3 py-1.5 border border-borderDefault"
          >
            {[25, 50, 100, 150, 200, 300, 500, 750, 1000, 2000, 5000, 10000, 25000].map((l) => (
              <option key={l} value={l}>Max {l} nodes</option>
            ))}
            <option value={0}>All</option>
          </select>
          {/* PRICE_MOVE sub-filters */}
          {(!eventType || eventType === "PRICE_MOVE") && (
            <>
              <select
                value={granularity}
                onChange={(e) => setGranularity(e.target.value)}
                className="bg-secondary text-textPrimary text-sm rounded-lg px-3 py-1.5 border border-borderDefault"
              >
                <option value="">All Timeframes</option>
                <option value="H1">H1</option>
                <option value="H4">H4</option>
                <option value="D1">D1</option>
              </select>
              <select
                value={moveType}
                onChange={(e) => setMoveType(e.target.value)}
                className="bg-secondary text-textPrimary text-sm rounded-lg px-3 py-1.5 border border-borderDefault"
              >
                <option value="">All Move Types</option>
                <option value="impulse">Impulse</option>
                <option value="gap">Gap</option>
                <option value="trend">Trend</option>
                <option value="reversal">Reversal</option>
                <option value="consolidation">Consolidation</option>
                <option value="breakout">Breakout</option>
              </select>
            </>
          )}
          <select
            value={graphLayoutMode}
            onChange={(e) => setGraphLayoutMode?.(e.target.value)}
            className="bg-secondary text-textPrimary text-sm rounded-lg px-3 py-1.5 border border-borderDefault"
            title="3D clustering mode"
          >
            <option value="relationships">Cluster by Relationships</option>
            <option value="event_type">Cluster by Event Type</option>
            <option value="currency">Cluster by Currency</option>
            <option value="time">Cluster by Time</option>
          </select>
          <select
            value={graphNodeSizeMode}
            onChange={(e) => setGraphNodeSizeMode?.(e.target.value)}
            className="bg-secondary text-textPrimary text-sm rounded-lg px-3 py-1.5 border border-borderDefault"
            title="3D node sizing"
          >
            <option value="relationships">Size by Links</option>
            <option value="impact">Size by Impact</option>
            <option value="uniform">Uniform Nodes</option>
          </select>
          <Button size="sm" onClick={fetchGraphData} disabled={loading}>
            {loading ? <RefreshCw size={14} className="animate-spin mr-1" /> : <Zap size={14} className="mr-1" />}
            Load Graph
          </Button>
          {hasGraphFilters && (
            <Button size="sm" variant="outline" onClick={clearGraphFilters} disabled={loading}>
              <X size={14} className="mr-1" />
              Clear Filters
            </Button>
          )}
          <div className="flex-1" />
          {/* 2D / 3D toggle */}
          <div className="flex rounded-lg border border-borderDefault overflow-hidden">
            <button
              onClick={() => setViewMode?.("2d")}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium transition-colors ${
                viewMode === "2d"
                  ? "bg-primary text-white"
                  : "bg-secondary/50 text-textSecondary hover:text-textPrimary"
              }`}
              title="2D Flow View"
            >
              <Monitor size={13} /> 2D
            </button>
            <button
              onClick={() => setViewMode?.("3d")}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium transition-colors ${
                viewMode === "3d"
                  ? "bg-primary text-white"
                  : "bg-secondary/50 text-textSecondary hover:text-textPrimary"
              }`}
              title="3D Force Graph"
            >
              <Box size={13} /> 3D
            </button>
          </div>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="p-1.5 rounded-lg bg-secondary/50 hover:bg-secondary text-textSecondary"
            title={expanded ? "Minimize" : "Expand"}
          >
            {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
        </div>
    </Card>
  );

  return (
    <div className="space-y-3">
      {!expanded && filterPanel}

      {/* Graph + Detail panel */}
      {(() => {
        const panel = (
          <div
            className={`flex gap-3 ${expanded ? "relative min-h-0 flex-1" : "relative"}`}
          >
        {expanded && (
          <button
            onClick={() => setExpanded(false)}
            className="absolute top-4 right-4 p-2 rounded-lg bg-secondary/90 hover:bg-secondary text-textPrimary border border-borderDefault shadow-lg"
            style={{ zIndex: 2147483647 }}
            title="Exit full screen"
          >
            <Minimize2 size={16} />
          </button>
        )}
        <Card className={`flex-1 overflow-hidden ${expanded ? "h-full" : "h-[700px]"}`}>
          {viewMode === "3d" ? (
            <KnowledgeGraph3D
              graphData={graphData}
              activeEdgeTypes={activeEdgeTypes}
              setActiveEdgeTypes={setActiveEdgeTypes}
              edges={edges}
              onNodeClick={onNodeClick}
              onNodeDoubleClick={onNodeDoubleClick}
              loading={loading}
              height={expanded ? expandedGraphHeight : 700}
              layoutMode={graphLayoutMode}
              nodeSizeMode={graphNodeSizeMode}
            />
          ) : loading ? (
            <div className="flex items-center justify-center h-full">
              <RefreshCw size={24} className="animate-spin text-primary" />
              <span className="ml-2 text-textSecondary">Loading graph...</span>
            </div>
          ) : nodes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-textSecondary gap-2">
              <GitBranch size={40} className="opacity-50" />
              <p>No graph data. Adjust filters and click "Load Graph".</p>
            </div>
          ) : (
            <ReactFlowProvider>
              <GraphCanvas
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodeClick={onNodeClick}
                onNodeDoubleClick={onNodeDoubleClick}
                graphData={graphData}
                activeEdgeTypes={activeEdgeTypes}
              />
            </ReactFlowProvider>
          )}
        </Card>

        {/* Selected node detail */}
        {selectedEvent && (
          <Card className={`w-80 shrink-0 p-4 space-y-3 overflow-y-auto ${expanded ? "h-full" : "h-[700px]"}`}>
            <div className="flex items-center justify-between">
              <span className={`text-xs font-semibold uppercase px-2 py-0.5 rounded ${EVENT_TYPE_BG[selectedEvent.eventType] || "bg-gray-500/20"}`}>
                {selectedEvent.eventTypeDisplay}
              </span>
              <button onClick={() => setSelectedEventId(null)} className="p-1 rounded hover:bg-secondary text-textSecondary hover:text-textPrimary transition-colors">
                <X size={14} />
              </button>
            </div>
            <h3 className="text-sm font-semibold text-textPrimary leading-tight">{selectedEvent.fullTitle}</h3>
            <p className="text-xs text-textSecondary">{formatTime(selectedEvent.timestamp)}</p>
            {selectedEvent.description && (
              <p className="text-xs text-textSecondary leading-relaxed">{stripHtml(selectedEvent.description)}</p>
            )}
            {selectedEvent.currencies?.length > 0 && (
              <div>
                <p className="text-[10px] text-textSecondary uppercase mb-1">Currencies</p>
                <div className="flex flex-wrap gap-1">
                  {selectedEvent.currencies.map((c) => (
                    <span key={c} className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">{c}</span>
                  ))}
                </div>
              </div>
            )}
            {selectedEvent.symbols?.length > 0 && (
              <div>
                <p className="text-[10px] text-textSecondary uppercase mb-1">Symbols</p>
                <div className="flex flex-wrap gap-1">
                  {selectedEvent.symbols.map((s) => (
                    <span key={s} className="text-xs bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded">{s}</span>
                  ))}
                </div>
              </div>
            )}
            {selectedEvent.impact != null && (
              <div>
                <p className="text-[10px] text-textSecondary uppercase mb-1">Impact</p>
                <SentimentBadge score={selectedEvent.impact} />
              </div>
            )}

            {/* Price move metadata badges */}
            {selectedEvent.eventType === "PRICE_MOVE" && selectedEvent.metadata && (
              <div className="flex flex-wrap gap-1.5">
                {selectedEvent.metadata.move_type && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 font-medium capitalize">
                    {selectedEvent.metadata.move_type}
                  </span>
                )}
                {selectedEvent.metadata.granularity && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 font-medium">
                    {selectedEvent.metadata.granularity}
                  </span>
                )}
                {selectedEvent.metadata.candle_count > 1 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-textSecondary">
                    {selectedEvent.metadata.candle_count} candles
                  </span>
                )}
              </div>
            )}

            {/* Connected edges summary */}
            {connectedEdges.length > 0 && (
              <div>
                <p className="text-[10px] text-textSecondary uppercase mb-1.5">
                  Connections ({connectedEdges.length})
                </p>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {connectedEdges.map((edge, i) => {
                    const isSource = edge.source === String(selectedEventId);
                    const otherId = isSource ? edge.target : edge.source;
                    const otherNode = nodes.find((n) => n.id === otherId);
                    const meta = EDGE_TYPE_META[edge.data?.edgeType] || {};
                    return (
                      <div
                        key={i}
                        className="flex items-start gap-2 p-1.5 rounded bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer text-xs"
                        onClick={() => setSelectedEventId(Number(otherId))}
                      >
                        <div
                          className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
                          style={{ backgroundColor: meta.color || "#6b7280" }}
                        />
                        <div className="min-w-0 flex-1">
                          <span className="text-[9px] font-medium uppercase" style={{ color: meta.color }}>
                            {isSource ? "→" : "←"} {meta.label || edge.data?.edgeType}
                          </span>
                          <p className="text-textPrimary truncate text-[11px] leading-tight">
                            {otherNode?.data?.title || `Event #${otherId}`}
                          </p>
                          {edge.data?.confidence != null && (
                            <div className="mt-1 w-full">
                              <ConfidenceBar value={edge.data.confidence} size="sm" showLabel />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <Button
              size="sm"
              variant="outline"
              className="w-full"
              onClick={() => onExploreChain(selectedEvent.id)}
              disabled={loadingChain}
            >
              {loadingChain ? <RefreshCw size={14} className="animate-spin mr-1" /> : <GitBranch size={14} className="mr-1" />}
              Explore Causal Chain
            </Button>
          </Card>
        )}
          </div>
        );
        if (!expanded || typeof document === "undefined") return panel;
        return createPortal(
          <div
            className="fixed inset-0 flex flex-col gap-3 overflow-hidden bg-backgroundBody p-4"
            style={{ zIndex: 2147483647 }}
          >
            {filterPanel}
            <div className="min-h-0 flex-1">{panel}</div>
          </div>,
          document.body,
        );
      })()}

      {/* Edge Type Toggles */}
      <Card className="p-3">
        <div className="flex flex-wrap gap-x-6 gap-y-2 items-center">
          <span className="text-xs font-medium text-textSecondary">Edges</span>
          <span className="text-[10px] text-textSecondary">(click to toggle):</span>
          {/* Extraction method toggles */}
          {(() => {
            const llmCount = (edges || []).filter((e) => e.data?.extractionMethod === "LLM").length;
            const ruleCount = (edges || []).filter((e) => e.data?.extractionMethod !== "LLM").length;
            return (
              <>
                {llmCount > 0 && (
                  <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-violet-500/40 bg-violet-500/10 text-violet-400 text-[11px] font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
                    AI edges ({llmCount})
                  </span>
                )}
                {ruleCount > 0 && (
                  <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-borderDefault bg-secondary/30 text-textSecondary text-[11px]">
                    Rule ({ruleCount})
                  </span>
                )}
              </>
            );
          })()}
          {Object.entries(EDGE_TYPE_META).map(([type, meta]) => {
            const count = edgeTypeCounts[type] || 0;
            if (count === 0) return null;
            const active = activeEdgeTypes.has(type);
            return (
              <button
                key={type}
                onClick={() => toggleEdgeType(type)}
                className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] transition-all
                  ${active
                    ? "border-borderDefault bg-secondary/50 text-textPrimary"
                    : "border-transparent bg-secondary/20 text-textSecondary opacity-50 line-through"
                  }`}
                title={`${meta.desc} — ${active ? "Click to hide" : "Click to show"}`}
              >
                <div className="w-4 h-0.5 rounded" style={{ backgroundColor: active ? meta.color : "#4b5563" }} />
                <span>{meta.label}</span>
                <span className="text-[9px] opacity-70">({count})</span>
              </button>
            );
          })}
        </div>
      </Card>
    </div>
  );
}


// ── Events Table Tab ───────────────────────────────────────────
function EventsTab({
  events, loading, totalEvents, page, totalPages,
  goToPage, searchTerm, setSearchTerm,
  eventType, setEventType, currency, setCurrency, symbol, setSymbol,
  daysBack,
  fetchEvents, onExploreChain, loadingChain,
  availableEventTypes = EVENT_TYPES,
}) {
  return (
    <div className="space-y-3">
      {/* Filters */}
      <Card className="p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-textSecondary" />
            <Input
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              onKeyDown={(e) => e.key === "Enter" && fetchEvents()}
            />
          </div>
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            className="bg-secondary text-textPrimary text-sm rounded-lg px-3 py-1.5 border border-borderDefault"
          >
            {availableEventTypes.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="bg-secondary text-textPrimary text-sm rounded-lg px-3 py-1.5 border border-borderDefault"
          >
            <option value="">All Currencies</option>
            {CURRENCIES.filter(Boolean).map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <SymbolAutocomplete
            value={symbol}
            onChange={setSymbol}
            daysBack={daysBack}
            currency={currency}
            eventType={eventType}
          />
          <Button size="sm" onClick={fetchEvents}>
            <Search size={14} className="mr-1" /> Filter
          </Button>
        </div>
      </Card>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-secondary/50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase">Type</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase">Title</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase">Timestamp</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase">Markets</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase">Impact</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-textSecondary uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-borderDefault">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-textSecondary">
                    <RefreshCw size={18} className="inline animate-spin mr-2" /> Loading events...
                  </td>
                </tr>
              ) : events.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-textSecondary">No events found</td>
                </tr>
              ) : events.map((evt) => (
                <tr key={evt.id} className="hover:bg-secondary/20 transition-colors">
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${EVENT_TYPE_BG[evt.event_type] || ""}`}>
                      {evt.event_type_display}
                    </span>
                  </td>
                  <td className="px-3 py-2 max-w-xs truncate text-textPrimary" title={evt.title}>{evt.title}</td>
                  <td className="px-3 py-2 text-textSecondary whitespace-nowrap">{formatTime(evt.timestamp)}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-0.5">
                      {evt.currencies_affected?.map((c) => (
                        <span key={c} className="text-[10px] bg-primary/20 text-primary px-1 rounded">{c}</span>
                      ))}
                      {evt.symbols?.map((s) => (
                        <span key={s} className="text-[10px] bg-amber-500/15 text-amber-400 px-1 rounded">{s}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {evt.impact_magnitude != null ? (
                      <SentimentBadge score={evt.impact_magnitude} />
                    ) : <span className="text-textSecondary">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onExploreChain(evt.id)}
                      disabled={loadingChain}
                      title="Explore causal chain"
                    >
                      <GitBranch size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-borderDefault bg-secondary/20">
            <span className="text-xs text-textSecondary">
              {totalEvents.toLocaleString()} events · Page {page} of {totalPages}
            </span>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => goToPage(page - 1)} disabled={page <= 1}>
                <ChevronLeft size={14} />
              </Button>
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                const p = page <= 3 ? i + 1 : page + i - 2;
                if (p < 1 || p > totalPages) return null;
                return (
                  <Button
                    key={p}
                    size="sm"
                    variant={p === page ? "default" : "ghost"}
                    onClick={() => goToPage(p)}
                  >
                    {p}
                  </Button>
                );
              })}
              <Button size="sm" variant="ghost" onClick={() => goToPage(page + 1)} disabled={page >= totalPages}>
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}


// ── Narratives Tab ─────────────────────────────────────────────
// ── Patterns Tab ───────────────────────────────────────────
function PatternsTab({ patterns, loading, fetchPatterns }) {
  return (
    <div className="space-y-3">
      <Card className="p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Repeat size={16} className="text-primary" />
          <span className="text-sm font-medium text-textPrimary">Recurring Causal Patterns</span>
          <span className="text-xs text-textSecondary">({patterns.length} detected)</span>
        </div>
        <Button size="sm" variant="outline" onClick={fetchPatterns} disabled={loading}>
          <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-textSecondary">
          <RefreshCw size={18} className="animate-spin mr-2" /> Detecting patterns...
        </div>
      ) : patterns.length === 0 ? (
        <Card className="p-8 text-center text-textSecondary">
          No recurring patterns found. Patterns emerge as more edges are created.
        </Card>
      ) : (
        <div className="space-y-3">
          {patterns.map((p, idx) => {
            const edgeMeta = EDGE_TYPE_META[p.edge_type] || { color: "#6b7280", label: p.edge_type };
            return (
              <Card key={idx} className="p-0 overflow-hidden">
                {/* Pattern header */}
                <div className="flex items-center gap-3 px-4 py-3 bg-secondary/30 border-b border-borderDefault">
                  <div className="flex items-center gap-1.5 flex-1 min-w-0">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${EVENT_TYPE_BG[p.source_type] || "bg-gray-500/20 text-gray-400"}`}>
                      {p.source_type}
                    </span>
                    <span className="text-textSecondary">→</span>
                    <span className="text-xs font-bold uppercase" style={{ color: edgeMeta.color }}>
                      {edgeMeta.label}
                    </span>
                    <span className="text-textSecondary">→</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${EVENT_TYPE_BG[p.target_type] || "bg-gray-500/20 text-gray-400"}`}>
                      {p.target_type}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-bold text-primary">{p.count}×</span>
                    <ConfidenceBar value={p.avg_confidence} showLabel />
                  </div>
                </div>
                {/* Examples */}
                {p.examples?.length > 0 && (
                  <div className="px-4 py-2.5 space-y-1.5">
                    <p className="text-[10px] uppercase text-textSecondary font-medium">Recent examples</p>
                    {p.examples.slice(0, 3).map((ex, j) => (
                      <div key={j} className="flex items-center gap-2 text-xs">
                        <span className="text-textPrimary truncate flex-1">{ex.source_title}</span>
                        <span className="text-textSecondary shrink-0">→</span>
                        <span className="text-textPrimary truncate flex-1">{ex.target_title}</span>
                        <span className="text-textSecondary shrink-0 text-[10px]">
                          {(ex.confidence * 100).toFixed(0)}%
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ── Narratives Tab ─────────────────────────────────────────
function NarrativesTab({ narratives, loading, timeframe, setTimeframe, fetchNarratives }) {
  return (
    <div className="space-y-3">
      <Card className="p-3 flex items-center gap-3">
        <span className="text-sm font-medium text-textSecondary">Timeframe:</span>
        {["daily", "weekly", "14d", "30d"].map((tf) => (
          <Button
            key={tf}
            size="sm"
            variant={timeframe === tf ? "default" : "outline"}
            onClick={() => { setTimeframe(tf); }}
          >
            {tf}
          </Button>
        ))}
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={fetchNarratives} disabled={loading}>
          <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-textSecondary">
          <RefreshCw size={18} className="animate-spin mr-2" /> Loading narratives...
        </div>
      ) : narratives.length === 0 ? (
        <Card className="p-8 text-center text-textSecondary">
          No narratives found for this timeframe.
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {narratives.map((n) => (
            <NarrativeCard key={n.id} narrative={n} />
          ))}
        </div>
      )}
    </div>
  );
}

function ConfluenceMeter({ score, label }) {
  const pct = Math.round((score || 0) * 100);
  const color = pct >= 70 ? "text-success" : pct >= 40 ? "text-warning" : "text-destructive";
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] uppercase text-textSecondary font-medium">{label}</span>
      <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${pct >= 70 ? "bg-success" : pct >= 40 ? "bg-warning" : "bg-destructive"}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs font-bold ${color}`}>{pct}%</span>
    </div>
  );
}

function OutlookRow({ horizon, data }) {
  if (!data) return null;
  const bias = data.bias || "neutral";
  const conf = Math.round((data.confidence || 0) * 100);
  const biasColor = bias === "bullish" ? "text-success" : bias === "bearish" ? "text-destructive" : "text-textSecondary";
  const biasIcon = bias === "bullish" ? "▲" : bias === "bearish" ? "▼" : "●";
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-24 text-textSecondary font-medium">{horizon}</span>
      <span className={`font-bold ${biasColor}`}>{biasIcon} {bias}</span>
      <span className="text-textSecondary">({conf}% confidence)</span>
    </div>
  );
}

function NarrativeCard({ narrative: n }) {
  const hasTa = n.technical_bias || (n.technical_analysis?.pair_summaries?.length > 0);
  const hasCot = n.cot_positioning && Object.keys(n.cot_positioning).length > 0;
  const hasOutlook = n.outlook && Object.keys(n.outlook).length > 0;

  return (
    <Card className="p-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-secondary/30 border-b border-borderDefault">
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold text-textPrimary">{n.currency}</span>
          <SentimentBadge score={n.sentiment_score} />
          {n.technical_bias && (
            <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
              n.technical_bias === "bullish" ? "bg-success/15 text-success" :
              n.technical_bias === "bearish" ? "bg-destructive/15 text-destructive" :
              "bg-secondary text-textSecondary"
            }`}>
              TA: {n.technical_bias}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-textSecondary">
          <span>{n.event_count} events</span>
          <span>·</span>
          <span>{n.timeframe}</span>
          {n.confluence_score > 0 && (
            <>
              <span>·</span>
              <span className={`font-bold ${n.confluence_score >= 0.7 ? "text-success" : n.confluence_score >= 0.4 ? "text-warning" : "text-destructive"}`}>
                {Math.round(n.confluence_score * 100)}% confluence
              </span>
            </>
          )}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Summary */}
        {n.summary && (
          <div>
            {n.summary.length > 200 && (
              <span className="inline-flex items-center gap-1 text-[10px] bg-violet-500/15 text-violet-400 px-1.5 py-0.5 rounded mb-1.5">
                <Brain size={10} /> AI-generated
              </span>
            )}
            <p className="text-sm text-textSecondary leading-relaxed whitespace-pre-line">{n.summary}</p>
          </div>
        )}

        {/* Confluence & Outlook Row */}
        {(hasOutlook || n.confluence_score > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-secondary/20 rounded-lg">
            {/* Predictive Outlook */}
            {hasOutlook && (
              <div>
                <p className="text-[10px] uppercase text-textSecondary font-medium mb-2">Predictive Outlook</p>
                <div className="space-y-1.5">
                  <OutlookRow horizon="Short-term" data={n.outlook.short_term} />
                  <OutlookRow horizon="Medium-term" data={n.outlook.medium_term} />
                  <OutlookRow horizon="Long-term" data={n.outlook.long_term} />
                </div>
              </div>
            )}
            {/* Confluence Meters */}
            {n.confluence_score > 0 && (
              <div className="space-y-2">
                <p className="text-[10px] uppercase text-textSecondary font-medium mb-2">Signal Alignment</p>
                <ConfluenceMeter score={n.confluence_score} label="Overall" />
                {n.technical_confluence > 0 && (
                  <ConfluenceMeter score={n.technical_confluence} label="TA TFs" />
                )}
              </div>
            )}
          </div>
        )}

        {/* Technical Analysis */}
        {hasTa && (
          <div>
            <p className="text-[10px] uppercase text-textSecondary font-medium mb-2">Multi-Timeframe Technical Analysis</p>
            <div className="space-y-3">
              {Object.entries(n.technical_analysis?.pairs || {}).map(([pair, tfData]) => {
                const pairSummary = (n.technical_analysis?.pair_summaries || []).find(ps => ps.pair === pair);
                const pairBias = pairSummary?.bias || "neutral";
                return (
                  <div key={pair} className="rounded-lg border border-borderDefault overflow-hidden">
                    {/* Pair header */}
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-secondary/30 border-b border-borderDefault">
                      <span className={`font-bold text-sm ${
                        pairBias === "bullish" ? "text-success" : pairBias === "bearish" ? "text-destructive" : "text-textSecondary"
                      }`}>{pair}</span>
                      <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                        pairBias === "bullish" ? "bg-success/15 text-success" :
                        pairBias === "bearish" ? "bg-destructive/15 text-destructive" :
                        "bg-secondary text-textSecondary"
                      }`}>{pairBias}</span>
                    </div>
                    {/* Timeframe rows */}
                    <div className="divide-y divide-borderDefault">
                      {["H1", "H4", "D1", "W1"].map(tf => {
                        const d = tfData[tf];
                        if (!d) return null;
                        const trendColor = d.trend === "up" ? "text-success" : d.trend === "down" ? "text-destructive" : "text-textSecondary";
                        return (
                          <div key={tf} className="px-3 py-1.5 grid grid-cols-[3rem_1fr] gap-2 text-xs">
                            <span className="font-mono font-bold text-textSecondary">{tf}</span>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                              {/* Trend + Signal */}
                              <span className={`font-semibold ${trendColor}`}>
                                {d.trend === "up" ? "▲" : d.trend === "down" ? "▼" : "●"} {d.trend}
                                {d.signal && d.signal !== "none" && (
                                  <span className="ml-1 opacity-80">({d.signal})</span>
                                )}
                              </span>
                              {/* RSI */}
                              {d.rsi != null && (
                                <span className="text-textSecondary">
                                  RSI <span className={`font-bold ${d.rsi > 70 ? "text-destructive" : d.rsi < 30 ? "text-success" : "text-textPrimary"}`}>{typeof d.rsi === "number" ? d.rsi.toFixed(0) : d.rsi}</span>
                                </span>
                              )}
                              {/* Structure */}
                              {d.structure && d.structure !== "unknown" && (
                                <span className="text-textSecondary">{d.structure}</span>
                              )}
                              {/* S/R levels */}
                              {d.support_levels?.length > 0 && (
                                <span className="text-success">
                                  S: {d.support_levels.slice(0, 2).map(s => s.level?.toFixed?.(s.level < 10 ? 4 : s.level < 100 ? 3 : s.level < 1000 ? 2 : 1) ?? s.level).join(" / ")}
                                </span>
                              )}
                              {d.resistance_levels?.length > 0 && (
                                <span className="text-destructive">
                                  R: {d.resistance_levels.slice(0, 2).map(r => r.level?.toFixed?.(r.level < 10 ? 4 : r.level < 100 ? 3 : r.level < 1000 ? 2 : 1) ?? r.level).join(" / ")}
                                </span>
                              )}
                              {/* Candlestick patterns */}
                              {d.candlestick_patterns?.length > 0 && (
                                <span className="text-violet-400 italic">{d.candlestick_patterns.join(", ")}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Chart images for this pair */}
                    {n.chart_images && n.chart_images[pair] && Object.keys(n.chart_images[pair]).length > 0 && (
                      <div className="px-3 py-2 bg-secondary/10 border-t border-borderDefault">
                        <p className="text-[10px] uppercase text-textSecondary font-medium mb-1.5">Charts</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(n.chart_images[pair]).map(([tf, url]) => {
                            const fullUrl = url?.startsWith("http") ? url : `${API_BASE_URL}${url}`;
                            return (
                              <a key={tf} href={fullUrl} target="_blank" rel="noopener noreferrer" className="group relative block">
                                <img
                                  src={fullUrl}
                                  alt={`${pair} ${tf} chart`}
                                  className="h-28 w-auto rounded border border-borderDefault hover:border-primary transition-colors cursor-pointer"
                                  loading="lazy"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                    e.target.nextElementSibling && (e.target.parentElement.innerHTML = `<div class="h-28 w-40 rounded border border-destructive/40 bg-destructive/10 flex items-center justify-center text-[10px] text-destructive p-2 text-center">Failed to load<br/>${tf} chart</div>`);
                                  }}
                                />
                                <span className="absolute top-1 left-1 text-[9px] font-mono font-bold bg-black/70 text-white px-1 py-0.5 rounded">{tf}</span>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* COT Positioning */}
        {hasCot && (
          <div>
            <p className="text-[10px] uppercase text-textSecondary font-medium mb-1.5">COT Positioning</p>
            <div className="flex flex-wrap gap-3 text-xs">
              <span className="text-textSecondary">
                Net: <span className={`font-bold ${n.cot_positioning.net_noncomm >= 0 ? "text-success" : "text-destructive"}`}>
                  {n.cot_positioning.net_noncomm?.toLocaleString()}
                </span>
              </span>
              {n.cot_positioning.change_noncomm != null && (
                <span className="text-textSecondary">
                  WoW: <span className={`font-bold ${n.cot_positioning.change_noncomm >= 0 ? "text-success" : "text-destructive"}`}>
                    {n.cot_positioning.change_noncomm >= 0 ? "+" : ""}{n.cot_positioning.change_noncomm?.toLocaleString()}
                  </span>
                </span>
              )}
              {n.cot_positioning.pct_long != null && (
                <span className="text-textSecondary">
                  % Long: <span className="font-bold text-textPrimary">{n.cot_positioning.pct_long}%</span>
                </span>
              )}
              {n.cot_positioning.bias && (
                <span className={`font-semibold px-1.5 py-0.5 rounded ${
                  n.cot_positioning.bias === "bullish" ? "bg-success/15 text-success" :
                  n.cot_positioning.bias === "bearish" ? "bg-destructive/15 text-destructive" :
                  "bg-secondary text-textSecondary"
                }`}>{n.cot_positioning.bias}</span>
              )}
            </div>
          </div>
        )}

        {/* Active Themes */}
        {n.active_themes?.length > 0 && (
          <div>
            <p className="text-[10px] uppercase text-textSecondary font-medium mb-1.5">Active Themes</p>
            <div className="flex flex-wrap gap-1.5">
              {n.active_themes.map((theme, i) => (
                <span
                  key={i}
                  className="text-xs px-2 py-1 rounded-lg bg-primary/10 text-primary border border-primary/20"
                >
                  {theme.theme || theme}
                  {theme.count != null && (
                    <span className="ml-1 opacity-60 font-bold">{theme.count}×</span>
                  )}
                  {theme.strength != null && (
                    <span className="ml-1 opacity-70">{(theme.strength * 100).toFixed(0)}%</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Key Events */}
        {n.key_events?.length > 0 && (
          <div>
            <p className="text-[10px] uppercase text-textSecondary font-medium mb-1.5">Key Events</p>
            <div className="space-y-1.5">
              {n.key_events.slice(0, 5).map((evt, i) => (
                <div key={i} className="flex items-start gap-2 text-xs group">
                  <span className="text-textSecondary mt-0.5">•</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-textPrimary">{evt.title || evt}</span>
                      {evt.event_type && (
                        <span className="text-[10px] px-1 py-0.5 rounded bg-secondary text-textSecondary font-mono">{evt.event_type}</span>
                      )}
                    </div>
                    {(evt.timestamp || evt.description) && (
                      <div className="flex items-center gap-2 mt-0.5">
                        {evt.timestamp && (
                          <span className="text-[10px] text-textSecondary">{formatTime(evt.timestamp)}</span>
                        )}
                        {evt.description && evt.description.length > 0 && (
                          <span className="text-[10px] text-textSecondary truncate max-w-[400px]" title={evt.description}>
                            — {evt.description}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {evt.impact_magnitude != null && (
                    <span className={`ml-auto shrink-0 font-mono ${evt.impact_magnitude >= 0 ? "text-success" : "text-destructive"}`}>
                      {evt.impact_magnitude >= 0 ? "+" : ""}{evt.impact_magnitude.toFixed(2)}
                    </span>
                  )}
                  {evt.id && (
                    <a
                      href={`#event-${evt.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        // Copy event ID to clipboard for reference
                        navigator.clipboard?.writeText(String(evt.id));
                      }}
                      className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-primary hover:text-primary/80"
                      title="Copy event ID"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Causal Chains count */}
        {n.causal_chains?.length > 0 && (
          <div className="text-xs text-textSecondary">
            <Zap size={12} className="inline mr-1" />
            {n.causal_chains.length} causal chain{n.causal_chains.length !== 1 ? "s" : ""} identified
          </div>
        )}

        <div className="text-[10px] text-textSecondary pt-1 border-t border-borderDefault">
          Updated: {formatTime(n.computed_at)}
        </div>
      </div>
    </Card>
  );
}


// ── Causal Chain Modal ─────────────────────────────────────────
function ChainModal({ chainData, onClose }) {
  const { root_event, chain, events } = chainData;

  // Build a simple chain visualization
  const chainEdges = chain || [];
  const eventIds = new Set();
  chainEdges.forEach((e) => {
    eventIds.add(e.source_event_id);
    eventIds.add(e.target_event_id);
  });
  if (root_event) eventIds.add(root_event.id);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-backgroundSurface border border-borderDefault rounded-2xl shadow-2xl w-[90%] max-w-3xl max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-borderDefault">
          <div>
            <h2 className="text-lg font-semibold text-textPrimary flex items-center gap-2">
              <GitBranch size={18} className="text-primary" />
              Causal Chain Explorer
            </h2>
            {root_event && (
              <p className="text-xs text-textSecondary mt-0.5 truncate max-w-lg">
                Root: {root_event.title}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary text-textSecondary">
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Root event */}
          {root_event && (
            <Card className="p-3 border-l-4" style={{ borderLeftColor: EVENT_TYPE_COLORS[root_event.event_type] || "#6b7280" }}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${EVENT_TYPE_BG[root_event.event_type] || ""}`}>
                  {root_event.event_type_display}
                </span>
                <span className="text-xs text-textSecondary">{formatTime(root_event.timestamp)}</span>
              </div>
              <p className="text-sm font-medium text-textPrimary">{root_event.title}</p>
              {root_event.currencies_affected?.length > 0 && (
                <div className="flex gap-1 mt-1">
                  {root_event.currencies_affected.map((c) => (
                    <span key={c} className="text-[10px] bg-primary/20 text-primary px-1 rounded">{c}</span>
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* Chain edges */}
          {chainEdges.length === 0 ? (
            <p className="text-sm text-textSecondary text-center py-4">No causal connections found for this event.</p>
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-medium text-textSecondary uppercase">
                {chainEdges.length} connection{chainEdges.length !== 1 ? "s" : ""} found
              </p>
              {chainEdges.map((edge, i) => {
                const srcEvt = events?.[edge.source_event_id];
                const tgtEvt = events?.[edge.target_event_id];
                return (
                  <div key={i} className="flex items-stretch gap-2">
                    {/* Source */}
                    <div className="flex-1 bg-secondary/30 rounded-lg p-2.5 border border-borderDefault">
                      <p className="text-[10px] text-textSecondary mb-0.5">
                        {srcEvt?.event_type_display || "Event"} · {srcEvt ? formatTime(srcEvt.timestamp) : ""}
                      </p>
                      <p className="text-xs text-textPrimary font-medium">
                        {srcEvt?.title || `Event #${edge.source_event_id}`}
                      </p>
                    </div>
                    {/* Arrow */}
                    <div className="flex flex-col items-center justify-center shrink-0 w-20">
                      <span className="text-[9px] text-primary font-semibold uppercase">{edge.edge_type}</span>
                      <div className="w-full h-px bg-primary relative my-0.5">
                        <div className="absolute right-0 -top-1 border-l-4 border-t-2 border-b-2 border-l-primary border-t-transparent border-b-transparent" />
                      </div>
                      {edge.confidence != null && (
                        <ConfidenceBar value={edge.confidence} showLabel />
                      )}
                    </div>
                    {/* Target */}
                    <div className="flex-1 bg-secondary/30 rounded-lg p-2.5 border border-borderDefault">
                      <p className="text-[10px] text-textSecondary mb-0.5">
                        {tgtEvt?.event_type_display || "Event"} · {tgtEvt ? formatTime(tgtEvt.timestamp) : ""}
                      </p>
                      <p className="text-xs text-textPrimary font-medium">
                        {tgtEvt?.title || `Event #${edge.target_event_id}`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-borderDefault flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}
